import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import CircularProgress from "@mui/material/CircularProgress";
import QuizIcon from "@mui/icons-material/Quiz";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import {
  updateAnswer,
  uploadAudio,
  uploadImage,
} from "../../redux/apiRequest";
import { useDispatch, useSelector } from "react-redux";
import { createAxios } from "../../createAxios";
import { getUserId } from "../../utils/authSession";
import AdminFormFrame from "../AdminFormFrame/AdminFormFrame";
import { useAnswerQuery } from "../../queries/useToeicQueries";
import { useQueryClient } from "@tanstack/react-query";
import { queryKeys } from "../../queries/queryKeys";

function UpdateAnswer() {
  const [name, setName] = useState("");
  const [oldName, setOldName] = useState("");
  const [answer, setAnswer] = useState("");
  const [file, setFile] = useState(null);
  const [audio, setAudio] = useState(null);
  const [image, setImage] = useState(null);
  const [sound, setSound] = useState(null);
  const [isImageLoading, setIsImageLoading] = useState(false);
  const [isAudioLoading, setIsAudioLoading] = useState(false);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const currentUser = useSelector((state) => state.user.signin.currentUser);
  const accessToken = currentUser?.metadata.tokens.accessToken;
  const userID = getUserId(currentUser);
  const axiosJWT = createAxios(currentUser);
  const { id } = useParams();
  const { data: answerDetails } = useAnswerQuery(id);
  const queryClient = useQueryClient();

  const handleFileChange = (event) => {
    setFile(event.target.files[0]);
  };

  const handleAudioChange = (event) => {
    setAudio(event.target.files[0]);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    const answerData = {
      answer_name: name,
      answer_content: answer,
      answer_image: image,
      answer_audio: sound,
    };

    if (file) {
      setIsImageLoading(true);
      const imageData = await uploadImage(file, "", dispatch);

      if (imageData.img_url) {
        answerData.answer_image = imageData.img_url;
      }
      setIsImageLoading(false);
    }

    if (audio) {
      setIsAudioLoading(true);
      const audioData = await uploadAudio(audio, dispatch);

      if (audioData.audio_url) {
        answerData.answer_audio = audioData.audio_url;
      }
      setIsAudioLoading(false);
    }

    const data = {
      name: oldName,
      update: answerData,
    };

    await updateAnswer(
      accessToken,
      userID,
      data,
      dispatch,
      navigate,
      axiosJWT
    );
    queryClient.invalidateQueries({ queryKey: queryKeys.answers.all });
  };

  useEffect(() => {
    if (!answerDetails) return;

    setOldName(answerDetails.name);
    setImage(answerDetails.image);
    setSound(answerDetails.audio);
    setAnswer(answerDetails.content);
    setName(answerDetails.name);
  }, [answerDetails]);


  return (
    <AdminFormFrame
      title="Update Test"
      eyebrow="Test management"
      description="Edit answer sheet information, answer key, image, and listening audio."
      icon={QuizIcon}
      color="#2196f3"
      bg="#edf7ff"
      backHref="/management/test"
      tips={["Review the answer key before saving.", "Upload a new image only when it needs replacing.", "Keep audio unchanged if the current file is correct."]}
    >
      <form onSubmit={handleSubmit}>
        <Box mb={2}>
          <TextField
            fullWidth
            label="Name"
            variant="outlined"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </Box>
        <Box mb={2}>
          <TextField
            fullWidth
            label="Answer"
            variant="outlined"
            multiline={true}
            rows={6}
            value={answer}
            onChange={(e) => setAnswer(e.target.value)}
          />
        </Box>
        <Box mb={2} sx={{ textAlign: "center", p: 2, borderRadius: 2, bgcolor: "#f8fbff", border: "1px solid #e5edf5" }}>
          <Typography gutterBottom variant="h6" sx={{ fontWeight: 700 }}>
            Current Answer
          </Typography>
          {image && (
            <img
              src={image}
              alt={name}
              style={{
                width: "100%",
                maxWidth: "600px",
                height: "auto",
                objectFit: "cover",
                marginBottom: "20px",
                borderRadius: "8px",
              }}
            />
          )}
        </Box>
        <Box mb={2}>
          <Button variant="outlined" component="label" fullWidth startIcon={<UploadFileIcon />}>
            {isImageLoading ? (
              <>
                <CircularProgress style={{ color: "#FFFFFF" }} size={24} />
                &nbsp;Updating Image...
              </>
            ) : (
              "Upload Image"
            )}
            <input type="file" hidden onChange={handleFileChange} />
          </Button>
        </Box>
        <Box mb={2} sx={{ textAlign: "center", p: 2, borderRadius: 2, bgcolor: "#f8fbff", border: "1px solid #e5edf5" }}>
          <Typography gutterBottom variant="h6" sx={{ fontWeight: 700 }}>
            Current Audio
          </Typography>
          <audio src={sound} controls></audio>
        </Box>
        <Box mb={2}>
          <Button variant="outlined" component="label" fullWidth startIcon={<UploadFileIcon />}>
            {isAudioLoading ? (
              <>
                <CircularProgress style={{ color: "#FFFFFF" }} size={24} />
                &nbsp;Updating Audio...
              </>
            ) : (
              "Upload Audio"
            )}
            <input type="file" hidden onChange={handleAudioChange} />
          </Button>
        </Box>
        <Box mb={2}>
          <Button
            type="submit"
            variant="contained"
            fullWidth
            sx={{ bgcolor: "#35c759", color: "#ffffff", fontWeight: 700, "&:hover": { bgcolor: "#2fb44f" } }}
          >
            Submit
          </Button>
        </Box>
      </form>
    </AdminFormFrame>
  );
}

export default UpdateAnswer;



